import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import BriaButton from "../../../../components/common/BriaButton/BriaButton.tsx";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import { useAuthService } from "../../../../hooks/useAuthService.tsx";
import styles from "./MfaRevertPage.module.scss";
import {useAppStore} from "../../../../hooks/useStores.tsx";
import {firebaseAuth} from "../../../../config/firebase.ts";
import LinkConstants from "../../../../constants/LinkConstants.ts";

const MfaRevertPage = () => {
	const { t } = useTranslation("translation", { keyPrefix: "mfa.mfaRevert" });
	const [loading, setLoading] = useState<boolean>(true);
	const [handledReverting, setHandledReverting] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [success, setSuccess] = useState<boolean>(false);
	const { revertMfaWithActionCode } = useAuthService();
	const { authStore } = useAppStore();
	const navigate = useNavigate();
	const location = useLocation();
	const oobCode = location.state.oobCode;

	useEffect(() => {
		const handleMfaRevert = async () => {
			setHandledReverting(true);
			if (!oobCode) {
				setError(t("error.invalidCode"));
				setLoading(false);
				return;
			}

			try {
				await revertMfaWithActionCode(oobCode);
				setSuccess(true);
			} catch (error: any) {
				if (error.code === "auth/expired-action-code") {
					setError(t("error.expiredCode"));
				} else if (error.code === "auth/invalid-action-code") {
					setError(t("error.invalidActionCode"));
				} else {
					setError(t("error.genericError"));
				}
			} finally {
				setLoading(false);
			}
		};

		if (!handledReverting) {
			handleMfaRevert();
		}
	}, [oobCode, revertMfaWithActionCode, t]);

	const handleContinue = () => {
		if (success) {
			if (firebaseAuth.currentUser) {
				authStore.logout();
			} else {
				navigate(RouterConstants.LOGIN.path, { replace: true });
			}
		} else {
			window.open(LinkConstants.BRIA_CONTACT_US_MFA, "_blank");
		}
	};

	return (
		<Box className={styles.container}>
			<LoadingPlaceholder isLoading={loading}>
				<Typography
					className={clsx(styles.title, { [styles.titleSuccess]: success, [styles.titleError]: !success })}
				>
					{success ? t("successTitle") : t("errorTitle")}
				</Typography>
				<Typography className={styles.subtitle}>{success ? t("successSubtitle") : error}</Typography>
				<BriaButton className={styles.button} buttonType="primaryMedium" onClick={handleContinue}>
					{success? t("loginButton") : t("contactSupportButton")}
				</BriaButton>
			</LoadingPlaceholder>
		</Box>
	);
};

export default observer(MfaRevertPage);
